import { ChangeEvent } from "react";
import { DestinationDistance } from "pages/api/distance-search";
import {
    SelectedFilterInterface,
    StoryblokDataSource
} from "storyblok/components";
import { useRouter } from "next/router";
import useTranslation from "next-translate/useTranslation";

import {
    ARROW_RIGHT,
    Button,
    CLOSE,
    Checkbox,
    Input,
    Radio,
    Toggle
} from "components/ui";
import {
    ButtonGroup,
    Filter,
    FilterTitle,
    InputWrapper,
    LeftContentTitle
} from "components/vacancies/VacancyOverview/VacancyOverviewFilters/VacancyOverviewFilters.styled";
import {
    hour_filters,
    max_hour_filters,
    min_hour_filters
} from "components/vacancies/VacancyOverview/filters";
import DistanceCalculator from "components/vacancies/VacancyOverview/DistanceCalculator";

import { useFilterOptions } from "helpers/hooks/useFilterOptions";
import { useMediaQueryContext } from "helpers/hooks";
import filterReset from "helpers/util/filterReset";

type VacancyOverviewFiltersProps = {
    label: string;
    searchTerm: string | null;
    setSearchTerm: (value: string) => void;
    setCalculatingFilters: (value: boolean) => void;
    zzp: boolean;
    setZzp: (value: boolean) => void;
    departments?: Array<StoryblokDataSource>;
    employmentTypes?: Array<StoryblokDataSource>;
    setLoading: (value: boolean) => void;
    /** Only needed for mobile view */
    vacanciesAmount?: number;
    /** Only needed for mobile view */
    closeFilters?: () => void;
    showFilters?: boolean;
    regions: Array<string>;
    possibleCertifications: Array<string>;
    setDestinationsWithDistances: (
        value: Array<DestinationDistance> | undefined
    ) => void;
    queryHasBeenMade: boolean;
};

function handleFilterChange({
    filterOptions,
    e,
    value,
    filter
}: {
    filterOptions: SelectedFilterInterface;
    e: ChangeEvent<HTMLInputElement>;
    filter:
        | "certificationsFilter"
        | "departmentsFilter"
        | "regionsFilter"
        | "employmentTypeFilter";
    value: number | string;
}) {
    const newFilterOptions = structuredClone(filterOptions);

    switch (filter) {
        case "certificationsFilter":
        case "departmentsFilter":
        case "regionsFilter":
        case "employmentTypeFilter":
            if (e.target.checked) {
                newFilterOptions[filter] = [
                    ...newFilterOptions[filter],
                    value as string
                ];
            } else {
                newFilterOptions[filter] = newFilterOptions[filter].filter(
                    filterElement => filterElement !== (value as string)
                );
            }
            return newFilterOptions;
    }
}

export function VacancyOverviewFilters({
    label,
    searchTerm,
    setSearchTerm,
    setCalculatingFilters,
    zzp,
    setZzp,
    departments,
    employmentTypes,
    setLoading,
    vacanciesAmount,
    closeFilters,
    showFilters,
    regions,
    possibleCertifications,
    setDestinationsWithDistances,
    queryHasBeenMade
}: VacancyOverviewFiltersProps) {
    const router = useRouter();
    const { t } = useTranslation();
    const { filterOptions, setFilterOptions } = useFilterOptions();

    const { isMobileView } = useMediaQueryContext();

    const {
        certificationsFilter,
        departmentsFilter,
        maximumHoursFilter,
        minimumHoursFilter,
        zzpHoursFilter,
        employmentTypeFilter
    } = filterOptions;

    function resetFilters() {
        setFilterOptions(filterReset());
        setSearchTerm("");
        router.replace({
            pathname: router.pathname,
            query: ""
        });
    }

    return (
        <>
            <LeftContentTitle>
                <h5>Filters</h5>
                <span aria-hidden="true" onClick={() => resetFilters()}>
                    Reset
                </span>
            </LeftContentTitle>

            {isMobileView && (
                <Filter>
                    <DistanceCalculator
                        regions={regions}
                        setDestinationsWithDistances={
                            setDestinationsWithDistances
                        }
                        queryHasBeenMade={queryHasBeenMade}
                    />
                </Filter>
            )}

            <Filter>
                <FilterTitle>Zoek {label.toLowerCase()}</FilterTitle>
                <div>
                    <Input
                        label=""
                        name="vacatures"
                        placeholder="Bijv. verpleegkundigen"
                        value={searchTerm || ""}
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        onChange={e => {
                            // handleClick();
                            router.replace({
                                pathname: router.pathname,
                                query: { search: e.target.value }
                            });
                            setSearchTerm(e.target.value);
                        }}
                    />
                </div>
            </Filter>
            <Filter>
                <div>
                    {/* eslint-disable-next-line jsx-a11y/interactive-supports-focus */}
                    <div
                        role="button"
                        onMouseDown={() => {
                            setCalculatingFilters(true);
                            if (!zzp) {
                                setFilterOptions(filterReset());
                                router.push("/zorgprofessionals/opdrachten");
                            } else {
                                setFilterOptions(filterReset());
                                router.push("/zorgprofessionals/vacatures");
                            }
                            setZzp(!zzp);
                        }}
                    >
                        <Toggle
                            name="zzp"
                            label={t("common:freelancejobs")}
                            activeValue="true"
                            currentValue={String(zzp)}
                        />
                    </div>
                </div>
            </Filter>
            {zzp ? (
                <Filter>
                    <FilterTitle>Aantal uur</FilterTitle>
                    <div>
                        <InputWrapper>
                            {hour_filters.map(f => (
                                // eslint-disable-next-line jsx-a11y/label-has-associated-control
                                // eslint-disable-next-line jsx-a11y/label-has-for
                                <Checkbox
                                    key={f.label}
                                    name="hours"
                                    label={f.label}
                                    activeValue={f.value}
                                    currentValue={zzpHoursFilter.find(
                                        hoursElement => hoursElement === f.value
                                    )}
                                    onChange={() => {
                                        const newZzpHoursFilter =
                                            zzpHoursFilter.includes(f.value)
                                                ? zzpHoursFilter.filter(
                                                      h => h !== f.value
                                                  )
                                                : [...zzpHoursFilter, f.value];

                                        setFilterOptions({
                                            ...filterOptions,
                                            zzpHoursFilter: newZzpHoursFilter
                                        });
                                    }}
                                />
                            ))}
                        </InputWrapper>
                    </div>
                </Filter>
            ) : (
                <>
                    {!!employmentTypes?.length && (
                        <Filter>
                            <FilterTitle>Dienstverband</FilterTitle>
                            <div>
                                <InputWrapper>
                                    {employmentTypes.map(employmentType => (
                                        <Checkbox
                                            label={employmentType.name}
                                            key={employmentType.value}
                                            name="employmentType"
                                            activeValue={employmentType.value}
                                            currentValue={employmentTypeFilter.find(
                                                employmentTypeElement =>
                                                    employmentTypeElement ===
                                                    employmentType.value
                                            )}
                                            onChange={e => {
                                                setFilterOptions(
                                                    handleFilterChange({
                                                        e,
                                                        filter: "employmentTypeFilter",
                                                        filterOptions,
                                                        value: employmentType.value
                                                    })
                                                );

                                                setLoading(false);
                                            }}
                                        />
                                    ))}
                                </InputWrapper>
                            </div>
                        </Filter>
                    )}
                    <Filter>
                        <FilterTitle>Vakgebied</FilterTitle>
                        <div>
                            {departments && (
                                <InputWrapper>
                                    {departments.map(f => (
                                        // eslint-disable-next-line jsx-a11y/label-has-associated-control
                                        // eslint-disable-next-line jsx-a11y/label-has-for

                                        <Checkbox
                                            label={f.name}
                                            key={f.name}
                                            name="departments"
                                            activeValue={f.value}
                                            currentValue={departmentsFilter.find(
                                                departmentElement =>
                                                    departmentElement ===
                                                    f.value
                                            )}
                                            onChange={e => {
                                                //toggle the department label in and out of the departmentfilter array
                                                setFilterOptions(
                                                    handleFilterChange({
                                                        e,
                                                        filter: "departmentsFilter",
                                                        filterOptions,
                                                        value: f.value
                                                    })
                                                );

                                                setLoading(false);
                                            }}
                                        />
                                    ))}
                                </InputWrapper>
                            )}
                        </div>
                    </Filter>

                    <Filter>
                        <FilterTitle>Niveau</FilterTitle>
                        <div>
                            <InputWrapper>
                                {possibleCertifications.map(certification => (
                                    // eslint-disable-next-line jsx-a11y/label-has-associated-control
                                    // eslint-disable-next-line jsx-a11y/label-has-for

                                    <Checkbox
                                        label={certification}
                                        key={certification}
                                        name="certifications"
                                        activeValue={certification}
                                        currentValue={certificationsFilter.find(
                                            certificationElement =>
                                                certificationElement ===
                                                certification
                                        )}
                                        onChange={e => {
                                            //toggle certifications in and out of certificationsFilter array

                                            setFilterOptions(
                                                handleFilterChange({
                                                    e,
                                                    filter: "certificationsFilter",
                                                    filterOptions,
                                                    value: certification
                                                })
                                            );

                                            setLoading(false);
                                        }}
                                    />
                                ))}
                            </InputWrapper>
                        </div>
                    </Filter>

                    <Filter>
                        <FilterTitle>Minimaal aantal uur</FilterTitle>
                        <div>
                            <InputWrapper>
                                {min_hour_filters.map(f => (
                                    // eslint-disable-next-line jsx-a11y/label-has-associated-control
                                    // eslint-disable-next-line jsx-a11y/label-has-for

                                    <Radio
                                        key={`minimum-hours-${f.label}`}
                                        label={f.label}
                                        name="minimum-hours"
                                        disabled={
                                            maximumHoursFilter
                                                ? f.value > maximumHoursFilter
                                                : false
                                        }
                                        checked={minimumHoursFilter === f.value}
                                        value={`${f.value}`}
                                        onChange={() => {
                                            setFilterOptions({
                                                ...filterOptions,
                                                minimumHoursFilter: f.value
                                            });
                                        }}
                                    />
                                ))}
                            </InputWrapper>
                        </div>
                    </Filter>

                    <Filter>
                        <FilterTitle>Maximaal aantal uur</FilterTitle>
                        <div>
                            <InputWrapper>
                                {max_hour_filters.map(f => (
                                    // eslint-disable-next-line jsx-a11y/label-has-associated-control
                                    // eslint-disable-next-line jsx-a11y/label-has-for

                                    <Radio
                                        key={`maximum-hours-${f.label}`}
                                        label={f.label}
                                        name="maximum-hours"
                                        disabled={
                                            minimumHoursFilter
                                                ? f.value < minimumHoursFilter
                                                : false
                                        }
                                        checked={maximumHoursFilter === f.value}
                                        value={`${f.value}`}
                                        onChange={() => {
                                            setFilterOptions({
                                                ...filterOptions,
                                                maximumHoursFilter: f.value
                                            });
                                        }}
                                    />
                                ))}
                            </InputWrapper>
                        </div>
                    </Filter>
                </>
            )}

            <ButtonGroup
                className={
                    showFilters ? "button-group-visible" : "button-group-hidden"
                }
            >
                <Button
                    buttonType="secondary"
                    size="round"
                    icon={CLOSE}
                    onClick={closeFilters}
                />
                <Button
                    buttonType="primary"
                    size="small"
                    onClick={closeFilters}
                    icon={ARROW_RIGHT}
                    reversedOrder
                >
                    <span>
                        {t("common:viewVacancies", {
                            count: vacanciesAmount
                        })}
                    </span>
                </Button>
            </ButtonGroup>
        </>
    );
}
